import { connected } from "./helpers/Helpers";
import { loader, store } from "./index";
import { showMessage } from "./Message";

class API {
  constructor() {
    // this.apiUrl = "http://lh.co";
    this.apiUrl = "https://larry.justsketch.me";
  }

  menuLoader(query, message) {
    document.querySelector(
      query
    ).innerHTML = `<p class='loader'><svg viewBox="0 0 24 24"><use xlink:href='images/feather-sprite.svg#activity'/></svg><br>${message}</p>`;
  }

  buttonLoader(query) {
    document.querySelector(query).innerHTML =
      '<span class="button-loader"><svg viewBox="0 0 24 24"><use xlink:href="images/feather-sprite.svg#activity"/></svg></span>';
  }

  async validateLicenseKey(key) {
    if (!connected) {
      showMessage("Internet connection required to validate key", 4000, 300);
      return null;
    }
    key = key.trim();
    // Only show progress for new activations, not the silent revalidation of a stored key
    if (key !== localStorage.getItem("licence_key")) {
      showMessage("Checking license key...", 4000, 300);
    }
    const headers = {
      "Accept": "application/json",
      "Content-Type": "application/x-www-form-urlencoded"
    };
    // A provider outage must not prevent another provider from validating the key.
    const request = async (url, options, resultField) => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { ...options, signal: controller.signal });
        // Providers also return JSON for invalid keys with a 400/404 status.
        if (!response.ok && response.status !== 400 && response.status !== 404) {
          return null;
        }
        const data = await response.json();
        const expectedType = resultField === "result" ? "string" : "boolean";
        return data && typeof data[resultField] === expectedType ? data : null;
      } catch (error) {
        return null;
      } finally {
        clearTimeout(timeout);
      }
    };
    const [gumroadResult1, gumroadResult2, lemonSqueezyResult, larryResult] =
      await Promise.all([
        request("https://api.gumroad.com/v2/licenses/verify", {
          body: `product_permalink=MjNqbv&license_key=${encodeURIComponent(key)}`,
          headers,
          method: "POST"
        }, "success"),
        request("https://api.gumroad.com/v2/licenses/verify", {
          body: `product_permalink=UFFST&license_key=${encodeURIComponent(key)}`,
          headers,
          method: "POST"
        }, "success"),
        request("https://api.lemonsqueezy.com/v1/licenses/validate", {
          body: `license_key=${encodeURIComponent(key)}`,
          headers,
          method: "POST"
        }, "valid"),
        request(`${this.apiUrl}/validate/${encodeURIComponent(key)}/`, {
          cache: "no-store"
        }, "result")
      ]);

    if (
      larryResult && (
        larryResult["result"] == "valid" ||
        larryResult["result"] == "expired"
      )
    ) {
      if (larryResult["result"] == "valid") {
        return larryResult;
      } else if (larryResult["result"] == "expired") {
        showMessage("This license key has expired");
      }
    } else if (gumroadResult1 && gumroadResult1["success"]) {
      if (
        this.checkDateValid(gumroadResult1.purchase.subscription_cancelled_at)
      ) {
        return gumroadResult1;
      } else {
        showMessage("This license key has expired");
      }
    } else if (gumroadResult2 && gumroadResult2["success"]) {
      return gumroadResult2;
    } else if (lemonSqueezyResult && lemonSqueezyResult["valid"]) {
      return lemonSqueezyResult;
    } else if ([gumroadResult1, gumroadResult2, lemonSqueezyResult, larryResult].includes(null)) {
      return null;
    } else {
      showMessage("This license key does not exist or has expired");
    }
    return false;
  }

  checkDateValid(date) {
    if (date === null) {
      return true;
    }
    return new Date(date) > new Date();
  }

  // Built-in libraries are static files in src/data, served from Cloudflare Pages
  async getLibrary() {
    return await fetch("/data/poses.json").then(response => {
      if (!response.ok) throw new Error(`poses.json returned ${response.status}`);
      return response.json();
    });
  }

  async getPropPacks() {
    return await fetch("/data/props.json").then(response => {
      if (!response.ok) throw new Error(`props.json returned ${response.status}`);
      return response.json();
    });
  }

  async getPoses(key, silent = false) {
    if (!silent && !store.state.savedPoses) {
      this.menuLoader("#pose-menu", "Loading poses...");
    }
    return await fetch(
      `${this.apiUrl}/userstore/poses/get/?key=${key.trim()}`
    ).then(result => result.json());
  }

  async getPose(key, id) {
    loader.startLoading(id, "Fetching your pose...");
    return await fetch(
      `${this.apiUrl}/userstore/poses/get/${id}/?key=${key.trim()}`
    ).then(result => {
      loader.stopLoading(id);
      return result.json();
    });
  }

  async savePose(key, name, values, category, id = null) {
    this.buttonLoader("#pose-menu button");

    return await fetch(
      `${this.apiUrl}/userstore/poses/save/?key=${key.trim()}`,
      {
        method: "POST",
        body: JSON.stringify({
          name,
          values,
          id,
          category
        })
      }
    ).then(data => {
      this.getPoses(key, true).then(data => {
        store.setState({ savedPoses: data });
      });
    });
  }

  async deletePose(key, id) {
    store.setState({
      savedPoses: store.state.savedPoses.filter(pose => pose.id != id)
    });
    return await fetch(
      `${this.apiUrl}/userstore/poses/delete/${id}/?key=${key.trim()}`
    ).then(result => result.json());
  }

  async getScenes(key, silent = false) {
    if (!silent && !store.state.savedScenes) {
      this.menuLoader("#scene-menu", "Loading scenes...");
    }
    return await fetch(
      `${this.apiUrl}/userstore/scenes/get/?key=${key.trim()}`
    ).then(result => result.json());
  }

  async getScene(key, id) {
    loader.startLoading(id, "Fetching your scene...");
    return await fetch(
      `${this.apiUrl}/userstore/scenes/get/${id}/?key=${key.trim()}`
    ).then(result => {
      loader.stopLoading(id);
      return result.json();
    });
  }

  async saveScene(key, name, values, category, id = null) {
    this.buttonLoader("#scene-menu button");
    console.log(values);
    return await fetch(
      `${this.apiUrl}/userstore/scenes/save/?key=${key.trim()}`,
      {
        method: "POST",
        body: JSON.stringify({
          name,
          values,
          id,
          category
        })
      }
    ).then(data => {
      this.getScenes(key, true).then(data => {
        store.setState({ savedScenes: data });
      });
    });
  }

  async deleteSavedScene(key, id) {
    store.setState({
      savedScenes: store.state.savedScenes.filter(scene => scene.id != id)
    });
    return await fetch(
      `${this.apiUrl}/userstore/scenes/delete/${id}/?key=${key.trim()}`
    ).then(result => result.json());
  }

  async getBodyTypes(key) {
    return await fetch(
      `${this.apiUrl}/userstore/bodytypes/get/?key=${key.trim()}`
    ).then(result => result.json());
  }

  async getBodyType(key, id) {
    loader.startLoading(id, "Fetching your body type...");
    return await fetch(
      `${this.apiUrl}/userstore/bodytypes/get/${id}/?key=${key.trim()}`
    ).then(result => {
      loader.stopLoading(id);
      return result.json();
    });
  }

  async saveBodyType(key, name, values, id = null) {
    return await fetch(
      `${this.apiUrl}/userstore/bodytypes/save/?key=${key.trim()}`,
      {
        method: "POST",
        body: JSON.stringify({
          name,
          values,
          id
        })
      }
    ).then(data => {
      this.getBodyTypes(key).then(data => {
        store.setState({ savedBodyTypes: data });
      });
    });
  }

  async deleteBodyType(key, id) {
    store.setState({
      savedBodyTypes: store.state.savedBodyTypes.filter(bodyType => bodyType.id != id)
    });
    return await fetch(
      `${this.apiUrl}/userstore/bodytypes/delete/${id}/?key=${key.trim()}`
    ).then(result => result.json());
  }
}

export default API;
